import { useEffect, useState } from 'react';
import { HiOutlineCreditCard, HiOutlineTrash, HiOutlineLockClosed } from "react-icons/hi2";
import Button from '../../../components/button/button';
import ConfirmDialog from "../../../components/confirm-dialog/confirm-dialog";
import { getPaymentMethods, detachPaymentMethod, checkoutSessionCreate } from '../../../services/billing';
import { useQuery } from "../../../helpers/hooks/common-hook";
import { SectionCard, Pill, Notice, LoadingState, EmptyState } from "../components";

const brandName = brand => (brand || 'card').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function PaymentMethodRow({ paymentMethod, onDelete, disabled }) {
    const { card } = paymentMethod;
    return (
        <li className="flex items-center gap-4 px-5 py-4">
            <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-600">
                <HiOutlineCreditCard className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">
                        {brandName(card.brand)} •••• {card.last4}
                    </p>
                    {paymentMethod.isDefault && <Pill tone="accent" icon={false}>Default</Pill>}
                </div>
                <p className="text-xs text-gray-500">
                    Expires {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                </p>
            </div>
            <button
                type="button"
                onClick={() => onDelete(paymentMethod.id)}
                disabled={disabled}
                title="Remove payment method"
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-900/20"
            >
                <HiOutlineTrash className="h-4 w-4" />
            </button>
        </li>
    );
}

export default function Index() {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const setup = useQuery().get("setup");

    const [loadState, setLoadState] = useState({ loading: true, failed: false });
    const [redirecting, setRedirecting] = useState(false);
    const [error, setError] = useState(null);
    const [deletingPaymentId, setDeletingPaymentId] = useState(null);

    async function fetchData() {
        const { response, status } = await getPaymentMethods();
        if (status === 200) {
            setPaymentMethods(response);
            setLoadState({ loading: false, failed: false });
        } else {
            setLoadState({ loading: false, failed: true });
        }
    }

    // cards are added through a Stripe Checkout setup session; Stripe redirects back here afterwards
    const handleCheckoutSessionCreate = async () => {
        setError(null);
        setRedirecting(true);
        const { response, status } = await checkoutSessionCreate();
        if (status === 200) {
            window.location = response.url;
        } else {
            setRedirecting(false);
            setError('Unable to start adding a payment method. Please try again.');
        }
    };

    const handleConfirmDelete = async confirmed => {
        const paymentMethodId = deletingPaymentId;
        setDeletingPaymentId(null);
        if (!confirmed) {
            return;
        }
        setError(null);
        const { status, response } = await detachPaymentMethod(paymentMethodId);
        if (status === 400 && response === 'cannot_remove_last_payment_method') {
            setError('Add another payment method before removing the only one on a paid plan.');
        } else if (status !== 200) {
            setError('Unable to remove the payment method. Please try again.');
        }
        await fetchData();
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (setup === '1') {
            handleCheckoutSessionCreate();
        }
    }, [setup]);

    const addButton = <Button text="Add payment method" onClick={handleCheckoutSessionCreate} loading={redirecting} disabled={redirecting} />;

    return (
        <div className="flex flex-col gap-6">
            {deletingPaymentId &&
                <ConfirmDialog
                    open={true}
                    onCompletion={handleConfirmDelete}
                    message="Remove this payment method?"
                />
            }

            {setup === '1' && redirecting && <Notice tone="info">Add a payment method to continue with your plan change. Redirecting to Stripe...</Notice>}
            {error && <Notice tone="danger">{error}</Notice>}

            {loadState.loading
                ? <LoadingState text="Loading payment methods..." />
                : loadState.failed
                    ? <Notice tone="danger" title="Unable to load payment methods"
                              action={<Button text="Retry" color="default" onClick={() => { setLoadState({ loading: true }); fetchData(); }} />} />
                    : <SectionCard
                        title="Payment methods"
                        description="Your default card is charged for plan fees, usage and add-ons."
                        action={paymentMethods.length > 0 ? addButton : null}
                        bodyClassName=""
                    >
                        {paymentMethods.length === 0
                            ? <EmptyState
                                icon={HiOutlineCreditCard}
                                title="No payment methods yet"
                                description="Add a card to upgrade to a paid plan or buy add-ons."
                                action={addButton}
                            />
                            : <ul className="divide-y divide-gray-100">
                                {paymentMethods.map(paymentMethod =>
                                    <PaymentMethodRow
                                        key={paymentMethod.id}
                                        paymentMethod={paymentMethod}
                                        disabled={redirecting}
                                        onDelete={setDeletingPaymentId}
                                    />
                                )}
                            </ul>
                        }
                    </SectionCard>
            }

            <p className="flex items-center gap-1.5 text-xs text-gray-500">
                <HiOutlineLockClosed className="h-3.5 w-3.5" />
                Card details are stored and processed by Stripe; BotCircuits never sees your full card number.
            </p>
        </div>
    );
}
